import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Spinner, CheckCircle, XCircle, Plus, Trash, Crown, Printer,
  CreditCard, UsersThree, Buildings, CalendarBlank, LinkSimple,
} from '@phosphor-icons/react';
import logisticsApi from '../../lib/logisticsApi';

const EXERCISE_TYPES = [
  { value: 'tabletop', label: 'Tabletop' },
  { value: 'field', label: 'Field' },
  { value: 'functional', label: 'Functional' },
  { value: 'full-scale', label: 'Full-Scale' },
];

const Section = ({ icon: Icon, title, children }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} weight="duotone" style={{ color: 'var(--ls-primary)' }} />
      <span className="ls-overline text-xs">{title}</span>
    </div>
    {children}
  </div>
);

const PrintableCard = ({ card }) => {
  const grouped = (card.pan || '').replace(/(.{4})/g, '$1 ').trim();
  return (
    <div
      className="ls-noise relative overflow-hidden"
      style={{
        aspectRatio: '1.586',
        borderRadius: 10,
        background: 'linear-gradient(135deg, #1C2228 0%, #13171C 45%, #0A0D10 100%)',
        border: '1px solid rgba(255,255,255,0.10)',
        padding: 16,
        color: '#FFFFFF',
        width: 280,
      }}
    >
      <div
        style={{
          position: 'absolute', top: -50, right: -30, width: 180, height: 180,
          background: 'radial-gradient(circle, rgba(255,94,0,0.30) 0%, transparent 62%)',
          pointerEvents: 'none',
        }}
      />
      <div className="relative flex flex-col h-full justify-between">
        <div className="flex items-start justify-between">
          <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 8 }} className="ls-overline">
            Simulated card
          </span>
          <span className="ls-mono font-bold" style={{ color: 'var(--ls-primary)', fontSize: 10, letterSpacing: '0.14em' }}>
            {card.scheme}
          </span>
        </div>
        <p className="ls-mono" style={{ fontSize: 14, fontWeight: 500, letterSpacing: '0.06em', textShadow: '1px 1px 0px rgba(0,0,0,0.55)' }}>
          {grouped}
        </p>
        <div className="flex items-end justify-between">
          <div>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 7 }} className="ls-overline">Holder</p>
            <p className="ls-mono" style={{ fontSize: 10, letterSpacing: '0.08em' }}>{card.holder}</p>
          </div>
          <div>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 7 }} className="ls-overline">CVV</p>
            <p className="ls-mono" style={{ fontSize: 10 }}>{card.cvv}</p>
          </div>
          <div className="text-right">
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 7 }} className="ls-overline">Expires</p>
            <p className="ls-mono" style={{ fontSize: 10 }}>{card.expiry}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateExerciseDrawer = ({ open, onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [subCompany, setSubCompany] = useState('');
  const [exerciseType, setExerciseType] = useState('');
  const [description, setDescription] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [emsExId, setEmsExId] = useState('');
  const [mpExId, setMpExId] = useState('');
  const [teamName, setTeamName] = useState('Logistics Team');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [leadId, setLeadId] = useState('');
  const [budget, setBudget] = useState('10000');
  const [cardsCount, setCardsCount] = useState(1);

  const [hubUsers, setHubUsers] = useState([]);
  const [emsExercises, setEmsExercises] = useState([]);
  const [mpExercises, setMpExercises] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const printRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setResult(null);
    setError(null);
    setLoadingData(true);
    Promise.all([
      logisticsApi.hubUsers().catch(() => []),
      logisticsApi.emsExercises().catch(() => []),
      logisticsApi.mpExercises().catch(() => []),
    ]).then(([users, ems, mp]) => {
      setHubUsers(users);
      setEmsExercises(ems);
      setMpExercises(mp);
      setLoadingData(false);
    });
  }, [open]);

  const toggleMember = (userId) => {
    setSelectedMembers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await logisticsApi.bootstrapExercise({
        name: name.trim(),
        company: company.trim() || undefined,
        sub_company: subCompany.trim() || undefined,
        exercise_type: exerciseType || undefined,
        description: description.trim() || undefined,
        linked_ems_exercise_id: emsExId || undefined,
        linked_mp_exercise_id: mpExId || undefined,
        start_at: startAt || undefined,
        end_at: endAt || undefined,
        team_name: teamName.trim() || 'Logistics Team',
        members: selectedMembers.map(uid => ({
          user_id: uid,
          is_lead: uid === leadId,
        })),
        budget_allocation: Number(budget) || 0,
        cards_per_team: cardsCount,
      });
      setResult(res);
      setStep(4);
      onCreated?.(res);
    } catch (e) {
      setError(e.message || 'Failed to create exercise');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    const w = window.open('', '_blank', 'width=800,height=600');
    w.document.write(`
      <html><head><title>Cards - ${name}</title>
      <style>
        body { font-family: monospace; padding: 20px; background: #fff; }
        .card-grid { display: flex; flex-wrap: wrap; gap: 24px; }
        .card { width: 320px; aspect-ratio: 1.586; border-radius: 10px;
          background: linear-gradient(135deg, #1C2228, #0A0D10);
          color: white; padding: 18px; position: relative; overflow: hidden;
          page-break-inside: avoid; }
        .card-top { display: flex; justify-content: space-between; font-size: 9px; opacity: 0.6; }
        .card-pan { font-size: 16px; letter-spacing: 2px; margin: 14px 0; }
        .card-bottom { display: flex; justify-content: space-between; margin-top: auto; }
        .card-label { font-size: 7px; opacity: 0.5; text-transform: uppercase; }
        .card-value { font-size: 11px; letter-spacing: 1px; }
        @media print { body { padding: 0; } }
      </style></head><body>
      <h2 style="margin-bottom:16px">${name} — Simulated Cards</h2>
      <div class="card-grid">
        ${(result?.cards || []).map(c => `
          <div class="card">
            <div class="card-top"><span>Simulated card</span><span style="color:#FF5E00;font-weight:bold">${c.scheme}</span></div>
            <div class="card-pan">${(c.pan || '').replace(/(.{4})/g, '$1 ').trim()}</div>
            <div class="card-bottom">
              <div><div class="card-label">Holder</div><div class="card-value">${c.holder}</div></div>
              <div><div class="card-label">CVV</div><div class="card-value">${c.cvv}</div></div>
              <div style="text-align:right"><div class="card-label">Expires</div><div class="card-value">${c.expiry}</div></div>
            </div>
          </div>
        `).join('')}
      </div></body></html>
    `);
    w.document.close();
    w.print();
  };

  const totalSteps = 3;
  const canNext = step === 1 ? name.trim().length > 0 : step === 2 ? true : true;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
          />
          <motion.aside
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="ls-glass fixed top-0 right-0 h-full z-50 flex flex-col w-full sm:w-[520px]"
            style={{ borderLeft: '1px solid var(--ls-border)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 h-14 shrink-0" style={{ borderBottom: '1px solid var(--ls-border)' }}>
              <div className="flex items-center gap-2">
                <Plus size={16} weight="bold" style={{ color: 'var(--ls-primary)' }} />
                <span className="ls-heading text-sm font-bold">Create Exercise</span>
                {step <= totalSteps && (
                  <span className="text-[11px] ml-2 px-2 py-0.5 rounded-sm" style={{ background: 'var(--ls-accent)', color: 'var(--ls-primary)' }}>
                    Step {step}/{totalSteps}
                  </span>
                )}
              </div>
              <button onClick={onClose} className="ls-btn-ghost !p-2"><X size={14} weight="bold" /></button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-5">
              {loadingData ? (
                <div className="flex items-center gap-2 py-8 justify-center">
                  <Spinner size={18} className="animate-spin" style={{ color: 'var(--ls-primary)' }} />
                  <span className="text-sm" style={{ color: 'var(--ls-muted)' }}>Loading…</span>
                </div>
              ) : step === 1 ? (
                <>
                  <Section icon={Buildings} title="Exercise Details">
                    <div className="space-y-3">
                      <label className="block">
                        <span className="ls-overline mb-1 block">Exercise Name *</span>
                        <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Birlik 2026" className="ls-input w-full" />
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                          <span className="ls-overline mb-1 block">Company</span>
                          <input type="text" value={company} onChange={e => setCompany(e.target.value)} placeholder="SpillConsult" className="ls-input w-full" />
                        </label>
                        <label className="block">
                          <span className="ls-overline mb-1 block">Sub-company</span>
                          <input type="text" value={subCompany} onChange={e => setSubCompany(e.target.value)} placeholder="Operations Div." className="ls-input w-full" />
                        </label>
                      </div>
                      <label className="block">
                        <span className="ls-overline mb-1 block">Exercise Type</span>
                        <select value={exerciseType} onChange={e => setExerciseType(e.target.value)} className="ls-input w-full">
                          <option value="">Select type…</option>
                          {EXERCISE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </label>
                      <label className="block">
                        <span className="ls-overline mb-1 block">Description</span>
                        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="Brief description…" className="ls-input w-full" style={{ resize: 'vertical' }} />
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                          <span className="ls-overline mb-1 block flex items-center gap-1"><CalendarBlank size={10} /> Start</span>
                          <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="ls-input w-full" />
                        </label>
                        <label className="block">
                          <span className="ls-overline mb-1 block flex items-center gap-1"><CalendarBlank size={10} /> End</span>
                          <input type="datetime-local" value={endAt} onChange={e => setEndAt(e.target.value)} className="ls-input w-full" />
                        </label>
                      </div>
                    </div>
                  </Section>

                  <Section icon={LinkSimple} title="Link to Existing Exercises (Optional)">
                    <div className="space-y-3">
                      <label className="block">
                        <span className="ls-overline mb-1 block">EMS Exercise</span>
                        <select value={emsExId} onChange={e => setEmsExId(e.target.value)} className="ls-input w-full">
                          <option value="">None — standalone</option>
                          {emsExercises.map(e => (
                            <option key={e.exercise_id} value={e.exercise_id}>{e.name} ({e.status})</option>
                          ))}
                        </select>
                      </label>
                      <label className="block">
                        <span className="ls-overline mb-1 block">Media Portal Exercise</span>
                        <select value={mpExId} onChange={e => setMpExId(e.target.value)} className="ls-input w-full">
                          <option value="">None — standalone</option>
                          {mpExercises.map(e => (
                            <option key={e.exercise_id} value={e.exercise_id}>{e.name} ({e.status})</option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </Section>
                </>
              ) : step === 2 ? (
                <>
                  <Section icon={UsersThree} title="Logistics Team">
                    <label className="block mb-3">
                      <span className="ls-overline mb-1 block">Team Name</span>
                      <input type="text" value={teamName} onChange={e => setTeamName(e.target.value)} className="ls-input w-full" />
                    </label>
                    <span className="ls-overline mb-2 block">Select Members ({selectedMembers.length} selected)</span>
                    <div className="max-h-[240px] overflow-y-auto ls-surface p-2 space-y-1">
                      {hubUsers.map(u => {
                        const sel = selectedMembers.includes(u.user_id);
                        return (
                          <div
                            key={u.user_id}
                            onClick={() => toggleMember(u.user_id)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-sm cursor-pointer transition-colors ${sel ? 'bg-[var(--ls-accent)]' : 'hover:bg-[var(--ls-elevated)]'}`}
                          >
                            <input type="checkbox" checked={sel} readOnly className="accent-[var(--ls-primary)]" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate" style={{ color: 'var(--ls-secondary)' }}>{u.name}</p>
                              <p className="text-xs truncate" style={{ color: 'var(--ls-muted)' }}>{u.email}</p>
                            </div>
                            <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: 'var(--ls-elevated)', color: 'var(--ls-muted)' }}>
                              {(u.roles || []).join(', ')}
                            </span>
                            {sel && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setLeadId(leadId === u.user_id ? '' : u.user_id); }}
                                className={`p-1 rounded-sm ${leadId === u.user_id ? 'text-[var(--ls-primary)]' : 'text-[var(--ls-muted)]'}`}
                                title={leadId === u.user_id ? 'Team lead' : 'Set as lead'}
                              >
                                <Crown size={14} weight={leadId === u.user_id ? 'fill' : 'regular'} />
                              </button>
                            )}
                          </div>
                        );
                      })}
                      {hubUsers.length === 0 && (
                        <p className="text-sm text-center py-4" style={{ color: 'var(--ls-muted)' }}>No users found in Hub</p>
                      )}
                    </div>
                  </Section>
                </>
              ) : step === 3 ? (
                <>
                  <Section icon={CreditCard} title="Budget & Cards">
                    <div className="space-y-3">
                      <label className="block">
                        <span className="ls-overline mb-1 block">Total Budget Allocation (Ÿ)</span>
                        <input type="number" step="0.01" min="0" value={budget} onChange={e => setBudget(e.target.value)} className="ls-input w-full ls-mono" placeholder="10000.00" />
                      </label>
                      <p className="text-xs" style={{ color: 'var(--ls-muted)' }}>
                        This amount is shared across all team members. Any member's spend deducts from the total balance visible to everyone.
                      </p>
                      <label className="block">
                        <span className="ls-overline mb-1 block">Number of Cards to Generate (1-10)</span>
                        <input type="number" min={1} max={10} value={cardsCount} onChange={e => setCardsCount(Math.min(10, Math.max(1, Number(e.target.value) || 1)))} className="ls-input w-full ls-mono" />
                      </label>
                      <p className="text-xs" style={{ color: 'var(--ls-muted)' }}>
                        All cards share the same wallet balance. {selectedMembers.length > 0 ? `Cards will be assigned to the first ${Math.min(cardsCount, selectedMembers.length)} team member(s).` : 'Cards will be labelled as team cards.'}
                      </p>
                    </div>
                  </Section>

                  <div className="ls-elevated p-4">
                    <p className="ls-overline mb-2">Summary</p>
                    <div className="space-y-1 text-sm">
                      <p><strong style={{ color: 'var(--ls-secondary)' }}>{name}</strong></p>
                      {company && <p style={{ color: 'var(--ls-muted)' }}>{company}{subCompany ? ` · ${subCompany}` : ''}</p>}
                      {exerciseType && <p style={{ color: 'var(--ls-muted)' }}>Type: {exerciseType}</p>}
                      <p style={{ color: 'var(--ls-muted)' }}>Team: {teamName} · {selectedMembers.length} members</p>
                      <p style={{ color: 'var(--ls-primary)' }} className="ls-mono font-bold">Budget: Ÿ{Number(budget || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      <p style={{ color: 'var(--ls-muted)' }}>{cardsCount} card{cardsCount > 1 ? 's' : ''} to generate</p>
                      {emsExId && <p style={{ color: 'var(--ls-muted)' }}>Linked to EMS exercise</p>}
                      {mpExId && <p style={{ color: 'var(--ls-muted)' }}>Linked to MP exercise</p>}
                    </div>
                  </div>

                  {error && (
                    <p className="text-sm mt-4 flex items-center gap-1.5" style={{ color: 'var(--ls-danger)' }}>
                      <XCircle size={14} weight="fill" /> {error}
                    </p>
                  )}
                </>
              ) : step === 4 && result ? (
                <div>
                  <div className="text-center mb-6">
                    <CheckCircle size={48} weight="duotone" style={{ color: 'var(--ls-success)' }} className="mx-auto" />
                    <h3 className="ls-heading text-xl font-bold mt-3">Exercise Created</h3>
                    <p className="text-sm mt-1" style={{ color: 'var(--ls-muted)' }}>{result.exercise.name}</p>
                  </div>

                  <div className="ls-elevated p-4 mb-4">
                    <p className="ls-overline mb-2">Wallet</p>
                    <p className="ls-mono text-2xl font-bold" style={{ color: 'var(--ls-primary)' }}>
                      Ÿ{((result.wallet?.available || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--ls-muted)' }}>
                      Team: {result.team?.name} · {(result.team?.member_user_ids || []).length} members
                    </p>
                  </div>

                  <div ref={printRef}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="ls-overline">Generated Cards ({(result.cards || []).length})</span>
                      <button onClick={handlePrint} className="ls-btn-ghost text-xs flex items-center gap-1">
                        <Printer size={12} weight="bold" /> Print Cards
                      </button>
                    </div>
                    <div className="space-y-3">
                      {(result.cards || []).map((c, i) => (
                        <PrintableCard key={i} card={c} />
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 shrink-0 flex items-center gap-3" style={{ borderTop: '1px solid var(--ls-border)' }}>
              {step > 1 && step <= totalSteps && (
                <button className="ls-btn-ghost" onClick={() => setStep(s => s - 1)}>Back</button>
              )}
              <div className="flex-1" />
              {step < totalSteps && (
                <button className="ls-btn-primary" onClick={() => setStep(s => s + 1)} disabled={!canNext}>
                  Next
                </button>
              )}
              {step === totalSteps && (
                <button className="ls-btn-primary" onClick={handleCreate} disabled={submitting || !name.trim()}>
                  {submitting ? <><Spinner size={14} className="animate-spin" /> Creating…</> : 'Create Exercise'}
                </button>
              )}
              {step === 4 && (
                <button className="ls-btn-primary" onClick={onClose}>Done</button>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CreateExerciseDrawer;
